import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GlassIconButton } from 'dashboard/components/common';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';

/**
 * Apply a positive or negative stock movement on a single
 * (variant, warehouse) cell. The matrix opens this on cell click.
 */
export default function MovementDialog({
  open, onClose, brandId, level, variantsById, warehousesById, onSaved,
}) {
  const [type, setType] = useState('production_in');
  const [qty, setQty] = useState(0);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setType('production_in'); setQty(0); setNote(''); }
  }, [open]);

  const apply = () => {
    if (!qty || qty <= 0) { toast.warning('Quantity must be positive'); return; }
    setSaving(true);
    API.applyShopInventoryMovement({
      brand_id: brandId,
      variant_id: level.variant_id,
      warehouse_id: level.warehouse_id,
      type,
      quantity: Number(qty),
      note,
    })
      .then(() => { toast.success('Stock updated'); onSaved && onSaved(); onClose(); })
      .catch((err) => toast.error(err?.message || 'Failed'))
      .finally(() => setSaving(false));
  };

  const v = variantsById[level?.variant_id];
  const w = warehousesById[level?.warehouse_id];

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle sx={{ pr: '48px' }}>
        Adjust stock
        <GlassIconButton label='Close'
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size='small'
        >
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing='10px'>
          <Typography variant='body2'>
            <strong>{v?.sku || 'SKU'}</strong> at <strong>{w?.name || 'warehouse'}</strong>
          </Typography>
          <FormControl size='small'>
            <InputLabel>Movement</InputLabel>
            <Select
              label='Movement'
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <MenuItem value='production_in'>Production received (+)</MenuItem>
              <MenuItem value='purchase_in'>Supplier shipment (+)</MenuItem>
              <MenuItem value='return_in'>Customer return (+)</MenuItem>
              <MenuItem value='adjust_in'>Manual increase (+)</MenuItem>
              <MenuItem value='adjust_out'>Manual decrease (−)</MenuItem>
              <MenuItem value='schedule_in'>Incoming scheduled (+incoming)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size='small'
            type='number'
            label='Quantity'
            inputProps={{ min: '1' }}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
          <TextField
            size='small'
            label='Note (optional)'
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder='e.g. batch #42'
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '14px 20px' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={apply} disabled={saving}>
          {saving ? 'Working…' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
